/**
 * Shapermint Theme Configuration
 * Official Shapermint brand theme with complete styling
 */

'use client';

export const shapermintTheme = {
  name: 'Shapermint',
  fontSizes: [10, 12, 14, 16, 18, 20, 24, 30, 36, 42, 52, 60, 68, 72, 76],
  fontWeights: [400, 600, 500, 700, 800],
  lineHeights: [14, 16, 18, 20, 22, 24, 28, 32, 38, 44, 52, 68, 76, 84, 88, 92],
  mediaQueries: {
    mobile: 640,
    tablet: 1024,
    desktop: 1280,
    xs: '480px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
  radius: {
    small: '4px',
    regular: '8px',
    full: '9999px',
  },
  zIndex: {
    modal: 50,
    overlay: 49,
  },
  colors: {
    pallete: {
      primary: {
        color: '#f7a08b',
        contrast: '#292929',
        soft: {
          color: '#fcd9d1',
          contrast: '#292929',
        },
      },
      secondary: {
        color: '#292929',
        contrast: '#ffffff',
        soft: {
          color: '#bbbbbb',
          contrast: '#292929',
        },
      },
      wine: {
        color: '#882A2B',
        contrast: '#ffffff',
      },
      red: {
        color: '#D3373C',
        contrast: '#ffffff',
      },
      creamy: {
        color: '#FFE9D8',
        contrast: '#292929',
      },
      ocean: {
        color: '#2B526D',
        contrast: '#ffffff',
      },
    },
    shades: {
      black: { color: '#000', contrast: '#fff' },
      white: { color: '#fff', contrast: '#292929' },
      100: { color: '#e5e5e5', contrast: '#292929' },
      200: { color: '#bbbbbb', contrast: '#292929' },
      250: { color: '#a6a6a6', contrast: '#292929' },
      300: { color: '#9e9e9e', contrast: '#292929' },
      400: { color: '#808080', contrast: '#ffffff' },
      500: { color: '#5a5a5a', contrast: '#ffffff' },
      550: { color: '#3a3a3a', contrast: '#ffffff' },
      700: { color: '#292929', contrast: '#ffffff' },
    },
    semantic: {
      positive: { color: '#2f806a', contrast: '#ffffff' },
      urgent: { color: '#c64844', contrast: '#ffffff' },
      attention: { color: '#f2d96f', contrast: '#292929' },
      informative: { color: '#0a609b', contrast: '#ffffff' },
      warning: { color: '#fbbf24', contrast: '#292929' }, // Gold/amber for stars
    },
    border: {
      color: '#e5e5e5',
      disabled: '#bbbbbb',
      highlight: '#292929',
    },
    background: {
      color: '#ffffff',
      disabled: '#efefef',
      primary: '#ffffff',
      secondary: '#f5f5f5',
    },
    text: {
      color: '#292929',
      disabled: '#a6a6a6',
      primary: '#292929',
      secondary: '#808080',
      tertiary: '#5a5a5a',
    },
    primary: '#f7a08b',
    secondary: '#292929',
  },
  component: {
    button: {
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: '600',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      minWidth: '120px',
      minHeight: '44px',
      size: {
        small: {
          fontSize: '0.75rem',
          padding: '0.625rem 2rem',
        },
        medium: {
          fontSize: '0.875rem',
          padding: '0.688rem 2rem',
        },
        large: {
          fontSize: '1rem',
          padding: '0.875rem 2rem',
        },
      },
      primary: {
        backgroundColor: '#f7a08b',
        color: '#292929',
        hoverBackgroundColor: '#fcd9d1',
        active: {
          backgroundColor: '#f7a08b',
          color: '#292929',
        },
        hover: {
          backgroundColor: '#fcd9d1',
          color: '#292929',
        },
      },
      secondary: {
        backgroundColor: '#292929',
        color: '#ffffff',
        hoverBackgroundColor: '#545454',
        active: {
          backgroundColor: '#292929',
          color: '#ffffff',
        },
        hover: {
          backgroundColor: '#545454',
          color: '#ffffff',
        },
      },
      tertiary: {
        backgroundColor: 'transparent',
        color: '#292929',
        hoverBackgroundColor: '#f5f5f5',
        active: {
          backgroundColor: 'transparent',
          color: '#292929',
        },
        hover: {
          backgroundColor: '#f5f5f5',
          color: '#292929',
        },
      },
      outline: {
        backgroundColor: 'transparent',
        color: '#292929',
        borderColor: '#e5e5e5',
        hoverBackgroundColor: '#f5f5f5',
        hoverBorderColor: '#292929',
        active: {
          backgroundColor: 'transparent',
          color: '#292929',
          borderColor: '#e5e5e5',
        },
        hover: {
          backgroundColor: '#f5f5f5',
          color: '#292929',
          borderColor: '#292929',
        },
      },
    },
    text: {
      variants: {
        body: { fontSize: '1rem', lineHeight: '1.5' },
        button: { fontSize: '0.875rem', lineHeight: '1.5' },
      },
      weights: {
        regular: 400,
        bold: 700,
        heavy: 900,
      },
    },
  },
  typography: {
    config: {
      weight: {
        heavy: 900,
        bold: 700,
        demi: 600,
        regular: 400,
      },
    },
    variants: {
      heading1: {
        fontSize: 42,
        lineHeight: '52px',
      },
      heading2: {
        fontSize: 36,
        lineHeight: '44px',
      },
      heading3: {
        fontSize: 30,
        lineHeight: '38px',
      },
      heading4: {
        fontSize: 24,
        lineHeight: '32px',
      },
      'body-regular': {
        fontSize: 16,
        lineHeight: '24px',
      },
      'body-small': {
        fontSize: 14,
        lineHeight: '22px',
      },
      button: {
        textTransform: 'uppercase',
      },
    },
  },
  fonts: {
    url: 'https://cdn.shapermint.com/assets/fonts/shapermint',
    config: [
      {
        family: 'AvenirNextLTPro',
        src: 'AvenirNextLTPro-Regular.woff2',
        weight: 'normal',
      },
      {
        family: 'AvenirNextLTPro',
        src: 'AvenirNextLTPro-Demi.otf',
        weight: 600,
      },
      {
        family: 'AvenirNextLTPro',
        src: 'AvenirNextLTPro-Bold.otf',
        weight: 700,
      },
      {
        family: 'AvenirNextLTPro',
        src: 'AvenirNextLTPro-Heavy.otf',
        weight: '800 900',
      },
    ],
  },
  assets: {
    cdn: 'https://cdn.shapermint.com/assets/common',
    images: {
      favicon:
        'https://cdn.shopify.com/s/files/1/0021/4889/2732/files/Favicon_pink_196x_fedce07d-d19c-4ca4-a53a-5a55869cfc1e.png?v=1619463864',
      logo: 'https://cdn.shapermint.com/assets/images/shapermint/shapermint_logo_black.svg',
      logoSize: {
        width: '204',
        height: '35',
      },
    },
  },
  label: {
    member: 'CLUB MEMBERS',
  },
  space: {
    xs: '0.25rem',    // 4px
    sm: '0.5rem',     // 8px
    md: '1rem',       // 16px
    lg: '1.5rem',     // 24px
    xl: '2rem',       // 32px
    '2xl': '3rem',    // 48px
    '3xl': '4rem',    // 64px
    '4xl': '6rem',    // 96px
    '5xl': '50rem',   // 800px - for large containers
  },
  shadow: {
    xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  },
};

export type ShapermintTheme = typeof shapermintTheme;

